import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { CalendarCheck, Check, ChevronDown, ChevronRight, ChevronUp, Clock, Dumbbell, Flame, Layers, Play, Plus, Repeat, RotateCcw, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { auth, workoutExecution, workoutPlans } from '../services/api';
import { estimateMinutes, exerciseLine, formatDuration, formatKg, muscleGroups, plural, safeMediaUrl, whyText } from '../components/WorkoutPlayer/playerLogic';
import { errorList } from '../components/ExerciseLibrary/shared';
import { TODAY_MINUTE_OPTIONS, TODAY_WORKOUT_DEFAULTS, fetchTodayRecovery, generateTodayProposal, saveTodayWorkout, trainedTodayMuscles } from '../components/WorkoutPlanStudio/todayWorkout';
import MemberLibrary, { ExerciseFicheDialog } from '../components/ExerciseLibrary/MemberLibrary';
import MyPlanGeneratorDialog from '../components/WorkoutPlanStudio/MyPlanGeneratorDialog';
import TodayWorkoutDialog from '../components/WorkoutPlanStudio/TodayWorkoutDialog';
import AddExerciseSheet from '../components/WorkoutPlayer/AddExerciseSheet';
import SubstituteSheet, { EMPTY_IN_PLAN } from '../components/WorkoutPlayer/SubstituteSheet';
import '../styles/ui.css';

/*
 * Treino — UM sítio para tudo o que é "o meu treino" (antes eram três entradas de menu a falar do mesmo: "Treino de hoje", "Plano de Treino" e, noutro lado, o histórico).
 *   Hoje       o que fazer agora, e um botão: Começar / Continuar (o ecrã de treino, que ocupa o ecrã todo, abre em /treino)
 *   Plano      os planos atribuídos, com os dias e os exercícios
 *   Histórico  os treinos já feitos
 *   Exercícios a biblioteca: pesquisar, filtrar e abrir a ficha (imagem, demonstração, músculos, passos, erros comuns)
 */

const TABS = [
  { to: '/meu-treino', label: 'Hoje', end: true },
  { to: '/meu-treino/plano', label: 'Plano' },
  { to: '/meu-treino/historico', label: 'Histórico' },
  { to: '/meu-treino/exercicios', label: 'Exercícios' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('pt-PT', { weekday: 'short', day: '2-digit', month: 'short' }) : '');

const Thumb = ({ url }) => (
  <span className="gm-thumb" aria-hidden="true">{url ? <img src={url} alt="" loading="lazy" draggable={false} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <Dumbbell size={20} />}</span>
);

const exerciseImage = (e) => safeMediaUrl(e.imageUrl || e.library?.imageUrl) || safeMediaUrl(e.gifUrl);

/** O exercício da biblioteca a que uma linha (de hoje ou do plano) corresponde; nulo num exercício escrito à mão. */
const libraryIdOf = (e) => [e.executedLibraryExerciseId, e.library?.id, e.libraryExerciseId, e.exerciseLibraryId, e.plannedLibraryExerciseId]
  .find((v) => Number.isInteger(v)) ?? null;

/**
 * A miniatura e o nome de um exercício como UM botão que abre a ficha (a mesma da biblioteca: como se faz, músculos, equipamento,
 * passos, alternativas). Sem ligação à biblioteca não há ficha: fica só o texto, sem um botão que não levaria a nada.
 */
function ExerciseOpen({ exercise, onOpen, children }) {
  const id = libraryIdOf(exercise);
  if (id == null) return <>{children}</>;
  return (
    <button type="button" className="gm-exercise-open" onClick={() => onOpen(id)} title="Ver como se faz" data-testid={`open-fiche-${id}`}>
      {children}
      <ChevronRight size={18} aria-hidden="true" className="gm-exercise-open-go" />
    </button>
  );
}

// ── Hoje ──────────────────────────────────────────────────────────────────

function TodayTab() {
  const navigate = useNavigate();
  const [state, setState] = useState('loading');
  const [today, setToday] = useState(null);
  const [active, setActive] = useState(null);
  const [activeUnknown, setActiveUnknown] = useState(false);   // não conseguimos confirmar se há treino em curso
  const [pickedId, setPickedId] = useState(null);      // o dia que o sócio escolheu (nulo = o sugerido)
  const [preview, setPreview] = useState(null);        // a pré-visualização desse dia, se não for o sugerido
  const [previewing, setPreviewing] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);     // «Gerar o meu treino» aberto (só no estado vazio)
  const [todayOpen, setTodayOpen] = useState(false);   // «Treino de hoje» aberto (uma sessão, pronta a começar)
  const [ficheId, setFicheId] = useState(null);         // a ficha do exercício em que se tocou
  const [regen, setRegen] = useState({ state: 'idle', minutes: null, error: null, fallback: null });   // regerar o treino de hoje com outro tempo

  // A SEMANA LEVE: o servidor diz se esta semana está marcada e, se não estiver, há quantas semanas o atleta
  // treina sem parar. Nada muda sozinho — a decisão é sempre um toque de uma pessoa.
  const [deload, setDeload] = useState(null);
  const [deloadBusy, setDeloadBusy] = useState(false);
  const loadDeload = useCallback(() => {
    workoutExecution.deload().then((r) => setDeload(r.data || null)).catch(() => setDeload(null));
  }, []);
  useEffect(() => { loadDeload(); }, [loadDeload]);
  const setLightWeek = async (on) => {
    setDeloadBusy(true);
    try {
      const r = on ? await workoutExecution.markDeload() : await workoutExecution.unmarkDeload();
      setDeload(r.data || null);
      toast.success(on ? 'Semana marcada como leve.' : 'Semana leve desfeita: volta a ser uma semana normal.');
      load({ silent: true });
    } catch (e) {
      toast.error(errorList(e, on ? 'Não foi possível marcar a semana.' : 'Não foi possível desfazer a semana leve.')[0]);
    } finally {
      setDeloadBusy(false);
    }
  };

  // `silent` recarrega sem passar pelo esqueleto de carregamento (é o que se usa ao regerar o treino de hoje).
  const load = useCallback((opts = {}) => {
    if (!opts.silent) setState('loading');
    setActiveUnknown(false);
    const localDate = new Date().toLocaleDateString('en-CA');   // yyyy-MM-dd da data local do sócio
    return Promise.all([
      workoutExecution.today(localDate).then((r) => r.data),
      // Se este pedido falhar, NÃO se pode afirmar que não há treino em curso: o ecrã diz que não
      // conseguiu confirmar (antes dizia «Começar treino» e o atleta levava um 409 ao carregar).
      workoutExecution.active().then((r) => r.data).catch(() => { setActiveUnknown(true); return null; }),
    ])
      .then(([t, a]) => {
        // Uma resposta 200 sem corpo não é «não tem plano»: é o servidor a não responder o que devia
        // (proxy a devolver HTML, versão antiga da API). Sem isto, o sócio lia «Ainda não tem um
        // plano de treino» e o ginásio ficava com a culpa de uma falha que não era dele.
        if (!t) { setState('error'); return null; }
        setToday(t); setActive(a && (a.executionId || a.id) ? a : null); setState('ok');
        return t;
      })
      .catch(() => { setState('error'); return null; });
  }, []);
  useEffect(() => { load(); }, [load]);

  const suggestedId = today?.days?.find((d) => d.suggested)?.id ?? today?.dayId ?? null;
  const shownToday = preview || today;
  const exercises = useMemo(() => (active?.exercises?.length ? active.exercises : shownToday?.exercises) || [], [active, shownToday]);

  const pick = (id) => {
    if (active || id === (pickedId ?? suggestedId)) return;
    setPickedId(id);
    if (id === suggestedId) { setPreview(null); return; }
    setPreviewing(true);
    workoutExecution.today(new Date().toLocaleDateString('en-CA'), id)
      .then((r) => setPreview(r.data))
      .catch(() => { toast.error('Não foi possível ver esse dia.'); setPickedId(null); setPreview(null); })
      .finally(() => setPreviewing(false));
  };

  // O treino arranca já ao chegar ao ecrã de treino: um só "Começar", sem segundo ecrã de "hoje".
  const startWorkout = () => navigate('/treino', { state: { start: true, dayId: active ? null : (pickedId ?? null), why: active ? null : (today?.suggestedBy ?? null) } });
  const minutes = useMemo(() => estimateMinutes(exercises), [exercises]);
  // Os músculos do dia: os grupos que o servidor mandou na biblioteca dos exercícios (sem dados, não há contagem).
  const muscles = useMemo(() => muscleGroups(exercises), [exercises]);

  /**
   * Regerar o treino de hoje com outro tempo, no próprio ecrã: o MESMO caminho do diálogo «Treino de hoje»
   * (motor → gravar no nome do atleta → o dia que o servidor devolveu), sem passar pelos parâmetros. O hub
   * recarrega e passa a mostrar o treino regerado. Se o treino de hoje continuar a ser o plano atribuído pelo
   * ginásio (a atribuição manda), diz-se — e o treino que se gerou tem o seu próprio botão para começar.
   */
  const regenerate = async (mins) => {
    setRegen({ state: 'saving', minutes: mins, error: null, fallback: null });
    try {
      const recovery = await fetchTodayRecovery();
      const proposal = await generateTodayProposal({
        ...TODAY_WORKOUT_DEFAULTS,
        sessionMinutes: mins,
        avoidMuscles: trainedTodayMuscles(recovery).map((m) => m.muscleId),
        seed: 0,
      });
      const saved = await saveTodayWorkout(proposal);
      if (!saved.ok) { setRegen({ state: 'error', minutes: mins, error: saved.message, fallback: null }); return; }
      setPickedId(null);
      setPreview(null);
      const fresh = await load({ silent: true });
      const shown = fresh?.dayId === saved.dayId;
      setRegen({ state: 'ok', minutes: mins, error: null, fallback: shown ? null : { dayId: saved.dayId, minutes: mins, assigned: !!fresh?.assignmentId } });
    } catch (e) {
      // Falhou: diz-se o que falhou e o seletor (e «Tentar outra vez») continuam lá para repetir o mesmo tempo.
      setRegen({ state: 'error', minutes: mins, error: errorList(e, 'Não foi possível gerar o treino de hoje. Tente outra vez.')[0], fallback: null });
    }
  };

  if (state === 'loading') return <div className="gm-skeleton" aria-busy="true" aria-label="A carregar o treino" />;
  if (state === 'error') {
    return (
      <div className="gm-empty" role="alert">
        <strong>Não foi possível carregar o treino</strong>
        Verifique a ligação e tente outra vez.
        <div><button type="button" className="gm-btn gm-btn-ghost" onClick={load}>Tentar novamente</button></div>
      </div>
    );
  }
  if (!today || (today.state === 'NoPlan' && !active)) {
    // O estado vazio passa a ter uma AÇÃO: o atleta pode propor e guardar o SEU plano (o servidor força o atleta
    // da sessão — nunca se gera para outra pessoa). O caminho do treinador continua a existir: é mais uma forma
    // de ter plano, e há sempre a biblioteca à mão para ver como se faz cada exercício.
    return (
      <>
        <div className="gm-empty" data-testid="hub-noplan">
          <strong>Ainda não tem um plano de treino</strong>
          {today?.message || 'Quando o seu treinador lhe atribuir um plano, o treino de hoje aparece aqui.'}
          <p className="gm-note" style={{ marginTop: 10 }}>Também pode gerar um plano para si, a partir do objetivo e dos dias que consegue treinar.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
            <button type="button" className="gm-btn gm-btn-primary" onClick={() => setTodayOpen(true)} data-testid="hub-generate-today">
              Gerar treino de hoje
            </button>
            <button type="button" className="gm-btn gm-btn-ghost" onClick={() => setPlanOpen(true)} data-testid="hub-generate">
              Gerar o meu treino
            </button>
            <NavLink className="gm-btn gm-btn-ghost" to="/meu-treino/exercicios">Ver exercícios</NavLink>
          </div>
        </div>
        {planOpen && (
          <MyPlanGeneratorDialog
            onClose={() => setPlanOpen(false)}
            onSaved={() => { setPlanOpen(false); toast.success('Plano guardado. Veja-o no separador Plano.'); load(); }}
          />
        )}
        {todayOpen && (
          <TodayWorkoutDialog
            onClose={() => setTodayOpen(false)}
            onStart={(dayId) => { setTodayOpen(false); navigate('/treino', { state: { start: true, dayId, why: null } }); }}
          />
        )}
      </>
    );
  }

  const dayName = active?.dayName || shownToday.dayName;
  const planTitle = active?.planTitle || shownToday.planTitle;
  const otherDay = !active && pickedId != null && pickedId !== suggestedId;
  const setsTotal = exercises.reduce((n, e) => n + (e.prescription?.setsTarget || 0), 0);
  const days = today.days || [];
  const trainedToday = !!today.completedTodayExecutionId;

  return (
    <div className="gm-grid">
      <section className="gm-hero gm-col-8" aria-labelledby="hub-today-title">
        <div className="gm-hero-eyebrow">{active ? 'Treino em curso' : otherDay ? 'Outro dia do plano' : trainedToday ? 'Já treinou hoje' : 'O treino de hoje'}</div>
        <h2 id="hub-today-title" className="gm-hero-title">{dayName}</h2>
        <p className="gm-hero-sub">{planTitle}{!active && !otherDay && whyText(today.suggestedBy) ? ` · ${whyText(today.suggestedBy)}` : ''}</p>
        <div className="gm-chip-row" style={{ marginBottom: 20 }}>
          <span className="gm-chip"><Clock size={14} aria-hidden="true" /> ~{minutes} min</span>
          <span className="gm-chip" data-testid="hub-count">
            <Dumbbell size={14} aria-hidden="true" /> {plural(exercises.length, 'exercício', 'exercícios')}
            {muscles.length > 0 ? ` • ${plural(muscles.length, 'músculo', 'músculos')}` : ''}
          </span>
          <span className="gm-chip"><Layers size={14} aria-hidden="true" /> {plural(setsTotal, 'série', 'séries')}</span>
          {active ? <span className="gm-chip"><Flame size={14} aria-hidden="true" /> em curso</span> : trainedToday && !otherDay ? <span className="gm-chip"><CalendarCheck size={14} aria-hidden="true" /> feito</span> : null}
        </div>
        <button type="button" className="gm-btn gm-btn-light" onClick={startWorkout} disabled={previewing || exercises.length === 0} data-testid="hub-start">
          {active ? <><RotateCcw size={18} aria-hidden="true" /> Continuar treino</> : <><Play size={18} fill="currentColor" aria-hidden="true" /> {trainedToday && !otherDay ? 'Treinar outra vez' : 'Começar treino'}</>}
        </button>
        {!active && activeUnknown ? (
          <p className="gm-note" data-testid="hub-active-unknown" role="status">
            Não foi possível confirmar se tem um treino em curso. Se tiver, o ecrã do treino retoma-o.
          </p>
        ) : null}

        {/* Semana leve (deload): marcada por uma pessoa, com a redução escrita e a possibilidade de desfazer. A
            sugestão mostra há quantas semanas o atleta treina sem parar e NÃO marca nada sem este toque. */}
        {deload && (deload.isLight || deload.suggestion) ? (
          <div className="gm-note" data-testid="hub-deload" role="status" style={{ marginTop: 12, border: '1px solid var(--border, #d8dbe6)', borderRadius: 12, padding: 12 }}>
            {deload.isLight ? (
              <>
                <strong data-testid="deload-is-light">Semana leve</strong>
                <span> · {deload.reduction}. As séries e a carga propostas já vêm reduzidas.</span>
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="gm-btn gm-btn-ghost" onClick={() => setLightWeek(false)} disabled={deloadBusy} data-testid="deload-undo">
                    Desfazer: esta semana volta ao normal
                  </button>
                </div>
              </>
            ) : (
              <>
                <span data-testid="deload-suggestion">{deload.suggestion.text}</span>
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="gm-btn gm-btn-ghost" onClick={() => setLightWeek(true)} disabled={deloadBusy} data-testid="deload-accept">
                    Sim, marcar esta semana como leve
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}
        {!active ? (
          <button type="button" className="gm-btn gm-btn-ghost-light" onClick={() => setTodayOpen(true)} data-testid="hub-generate-today" style={{ marginTop: 12 }}>
            Gerar treino de hoje
          </button>
        ) : null}

        {/* O TEMPO, no próprio ecrã: escolher 30 a 120 minutos regera já o treino de hoje — o mesmo caminho do
            diálogo, sem passar pelos parâmetros. Só quando não há uma sessão a decorrer (não se gera o que se está a fazer). */}
        {!active ? (
          <div className="gm-chip-row" style={{ marginTop: 12 }} role="group" aria-label="Tempo do treino de hoje" data-testid="hub-time">
            <span className="gm-chip"><Clock size={14} aria-hidden="true" /> Treino de hoje com</span>
            {TODAY_MINUTE_OPTIONS.map((m) => (
              <button key={m} type="button" className="gm-btn gm-btn-ghost-light" aria-pressed={regen.state === 'ok' && regen.minutes === m}
                onClick={() => regenerate(m)} disabled={regen.state === 'saving'} data-testid={`hub-time-${m}`}>
                {m} min
              </button>
            ))}
          </div>
        ) : null}
        {!active && regen.state === 'saving' ? (
          <p className="gm-note" role="status" data-testid="hub-time-status">A gerar o treino de hoje para {regen.minutes} min…</p>
        ) : null}
        {!active && regen.state === 'ok' && !regen.fallback ? (
          <p className="gm-note" role="status" data-testid="hub-time-status">Treino de hoje regerado para {regen.minutes} min.</p>
        ) : null}
        {!active && regen.error ? (
          <div role="alert" data-testid="hub-time-error">
            <p className="gm-note">{regen.error}</p>
            <button type="button" className="gm-btn gm-btn-ghost-light" onClick={() => regenerate(regen.minutes)}>Tentar outra vez</button>
          </div>
        ) : null}
        {!active && regen.fallback ? (
          <div role="status" data-testid="hub-time-fallback">
            <p className="gm-note">
              O treino de {regen.fallback.minutes} min ficou guardado no seu plano, mas {regen.fallback.assigned ? 'o treino de hoje continua a ser o que o ginásio lhe atribuiu' : 'o treino de hoje que aparece aqui não é esse'}.
            </p>
            <button type="button" className="gm-btn gm-btn-ghost-light" onClick={() => navigate('/treino', { state: { start: true, dayId: regen.fallback.dayId, why: null } })} data-testid="hub-time-start">Começar o treino que gerei</button>
          </div>
        ) : null}
      </section>

      <section className="gm-card gm-col-4" aria-label="Dias do plano">
        <div className="gm-card-head"><h3 className="gm-card-title">{active ? 'Outros dias' : 'Dias do plano'}</h3></div>
        {active ? (
          <p className="gm-note" data-testid="hub-active-note">Continue este treino. Para treinar outro dia, termine-o ou abandone-o dentro do treino (☰ → Abandonar).</p>
        ) : days.length > 0 ? (
          <div className="gm-list" role="radiogroup" aria-label="Dia do treino">
            {days.map((d) => {
              const on = d.id === (pickedId ?? suggestedId);
              return (
                <button key={d.id} type="button" role="radio" aria-checked={on} className="gm-list-row gm-pick" onClick={() => pick(d.id)} data-testid={`hub-day-${d.id}`}>
                  <span className="gm-pick-mark" aria-hidden="true">{on ? <Check size={12} strokeWidth={3} /> : null}</span>
                  <span className="gm-list-main">
                    <span className="gm-list-title">{d.name}{d.suggested ? ' ★' : ''}</span>
                    <span className="gm-list-sub">{plural(d.exerciseCount ?? 0, 'exercício', 'exercícios')}{d.suggested ? ' · sugerido' : ''}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : days.length === 1 ? <p className="gm-note" data-testid="hub-one-day">Só há um dia neste plano.</p>
          : <p className="gm-note" data-testid="hub-no-days">Este plano ainda não tem dias definidos — peça ao seu treinador para o completar.</p>}
        {!active && days.length > 1 ? <p className="gm-note" style={{ marginTop: 10 }}>Escolha o dia que quer treinar; ★ é o que o plano sugere para hoje.</p> : null}
      </section>

      <section className="gm-card gm-col-12" aria-label="Exercícios de hoje">
        <div className="gm-card-head"><h3 className="gm-card-title">Exercícios</h3></div>
        {previewing ? <div className="gm-skeleton" aria-busy="true" aria-label="A carregar o dia" /> : exercises.length === 0 ? (
          <p className="gm-note" data-testid="hub-no-exercises">Este dia ainda não tem exercícios.</p>
        ) : (
        <ol className="gm-list">
          {exercises.map((e, i) => (
            <li key={e.planExerciseId ?? e.id ?? i} className="gm-list-row" data-testid={`hub-exercise-${e.planExerciseId ?? e.id ?? i}`}>
              <ExerciseOpen exercise={e} onOpen={setFicheId}>
                <Thumb url={exerciseImage(e)} />
                <span className="gm-list-main">
                  <span className="gm-list-title gm-list-name">{e.name}</span>
                  <span className="gm-list-sub">{exerciseLine(e) || 'Sem prescrição'}</span>
                </span>
              </ExerciseOpen>
              {e.status === 'Completed' ? <span className="gm-chip is-good">feito</span> : e.status === 'Skipped' ? <span className="gm-chip">saltado</span> : null}
            </li>
          ))}
        </ol>)}
      </section>

      {ficheId != null && <ExerciseFicheDialog exerciseId={ficheId} onClose={() => setFicheId(null)} />}

      {todayOpen && (
        <TodayWorkoutDialog
          onClose={() => setTodayOpen(false)}
          onStart={(dayId) => { setTodayOpen(false); navigate('/treino', { state: { start: true, dayId, why: null } }); }}
        />
      )}
    </div>
  );
}

// ── Plano ─────────────────────────────────────────────────────────────────

/**
 * A prescrição do plano no formato que o player já usa (`prescription.setsTarget/repsMin/repsMax/restSeconds`),
 * para a duração estimada e o texto baterem com os do ecrã «Hoje» — não há duas contas diferentes para a mesma coisa.
 */
const asPlayerExercise = (ex) => ({
  ...ex,
  trackingType: ex.trackingType || 'weight-reps',
  prescription: {
    setsTarget: ex.setsTarget ?? ex.sets ?? null,
    repsMin: ex.repsMin ?? ex.reps ?? null,
    repsMax: ex.repsMax ?? ex.reps ?? null,
    restSeconds: ex.restSeconds ?? null,
    summary: ex.prescriptionSummary || null,
  },
});

/** A linha da prescrição: a do servidor quando existe; senão compõe-se do que o plano tem (sem inventar cargas). */
const prescriptionLine = (ex) => {
  if (ex.prescriptionSummary) return ex.prescriptionSummary;
  const sets = ex.setsTarget ?? ex.sets ?? null;
  const reps = ex.repsMin && ex.repsMax && ex.repsMin !== ex.repsMax
    ? `${ex.repsMin}–${ex.repsMax}`
    : (ex.repsMin ?? ex.repsMax ?? ex.reps ?? null);
  const parts = [];
  if (sets && reps) parts.push(`${sets} × ${reps}`);
  else if (reps) parts.push(String(reps));
  if (ex.restSeconds) parts.push(`${ex.restSeconds}s de descanso`);
  else if (ex.restTime) parts.push(`descanso ${ex.restTime}`);
  if (ex.targetWeightKg != null) parts.push(`${formatKg(ex.targetWeightKg)} kg`);
  return parts.join(' · ') || 'Sem prescrição';
};

/**
 * De quem é o plano — a MESMA regra do servidor (`TrainingAuthz.CanEditPlanExercisesAsync`), para o ecrã não mostrar
 * um botão que o servidor recusaria:
 *   `editable`  o atleta pode mexer nos EXERCÍCIOS deste plano: é um plano de atleta (MemberPlan), dele, ACTIVO,
 *               que ELE criou (`createdByUserId`) e que não nasceu de um modelo do ginásio.
 *   `fromGym`   é o plano dele, mas o autor é o ginásio (o treinador) — as ações não aparecem e diz-se de quem é.
 * Um plano que ele próprio criou mas está inativo não é nem editável nem «do ginásio»: fica como está, sem ações.
 */
const planOwnership = (plan, meId, canEditAssigned = false) => {
  const mine = !!meId && plan.userId === meId;
  const mineAuthored = mine && plan.createdByUserId === meId && !plan.sourceTemplateId;
  return {
    mine,
    // A permissão da ficha do utilizador («pode alterar o treino atribuído») dá-lhe também o plano que o ginásio lhe atribuiu.
    editable: (mineAuthored || (mine && canEditAssigned)) && plan.planType === 'MemberPlan' && plan.isActive === true,
    fromGym: mine && !mineAuthored && !(canEditAssigned && plan.planType === 'MemberPlan' && plan.isActive === true),
  };
};

/**
 * Um dia do plano, como o atleta o quer ver: o que vai fazer, quanto demora, e o botão para começar
 * esse dia — sem passar pelo ecrã de hoje nem por um segundo «Começar». No plano DELE pode ainda acrescentar
 * um exercício ao dia e trocar um exercício (a mesma procura da biblioteca e as mesmas sugestões do treino).
 */
function PlanDay({ day, index, planId, editable, onStart, onChanged }) {
  const exercises = day.exercises || [];
  const minutes = exercises.length ? estimateMinutes(exercises.map(asPlayerExercise)) : 0;
  const dayName = day.dayName || `Dia ${index + 1}`;
  const [addOpen, setAddOpen] = useState(false);
  const [swap, setSwap] = useState(null);       // o exercício do plano que se está a trocar
  const [removing, setRemoving] = useState(null); // o exercício à espera de confirmação para sair do plano (nada sai sem ela)
  const [removeError, setRemoveError] = useState(null);   // a recusa do servidor, à vista, sem perder a escolha
  const [busy, setBusy] = useState(false);
  const [ficheId, setFicheId] = useState(null);   // a ficha do exercício em que se tocou
  const [notice, setNotice] = useState(null);   // o que ficou feito, à vista (a lista recarrega por baixo)

  // O que já está neste dia: o servidor recusa repetidos e a folha desativa-os com o motivo.
  const libraryIds = exercises.map((e) => e.exerciseLibraryId).filter((id) => Number.isInteger(id));

  const add = async (libraryExerciseId, prescription) => {
    setBusy(true);
    setNotice(null);
    try {
      await workoutPlans.addExercise(planId, day.id, { exerciseLibraryId: libraryExerciseId, ...(prescription || {}) });
      setAddOpen(false);
      setNotice('Exercício acrescentado a este dia.');
      onChanged?.();
      return { ok: true };
    } catch (e) {
      // O servidor recusou (ex.: sem permissão, exercício indisponível): a mensagem dele fica à vista na folha,
      // com a escolha intacta, e o botão continua lá para repetir.
      return { ok: false, message: errorList(e, 'Não foi possível acrescentar o exercício. Tente outra vez.')[0] };
    } finally {
      setBusy(false);
    }
  };

  const replace = async (libraryExerciseId) => {
    if (!swap) return { ok: false, message: 'Escolha o exercício a trocar.' };
    setBusy(true);
    setNotice(null);
    try {
      await workoutPlans.replaceExercise(planId, swap.id, libraryExerciseId);
      setSwap(null);
      setNotice('Exercício trocado neste dia.');
      onChanged?.();
      return { ok: true };
    } catch (e) {
      return { ok: false, message: errorList(e, 'Não foi possível trocar o exercício. Tente outra vez.')[0] };
    } finally {
      setBusy(false);
    }
  };

  // Remover um exercício do SEU plano. A confirmação diz o que sai (o exercício, por nome, e o dia); até a confirmar
  // nada foi pedido ao servidor. O plano do ginásio não tem esta ação (só aparece quando `editable`).
  const remove = async (exercise) => {
    setBusy(true);
    setNotice(null);
    setRemoveError(null);
    try {
      await workoutPlans.removeExercise(planId, exercise.id);
      setRemoving(null);
      setNotice(`«${exercise.name}» saiu de ${dayName}.`);
      onChanged?.();
      return { ok: true };
    } catch (e) {
      // O servidor recusou (sem permissão, plano que já não é editável): diz-se porquê e deixa-se repetir.
      const message = errorList(e, 'Não foi possível remover o exercício. Tente outra vez.')[0];
      setRemoveError(message);
      return { ok: false, message };
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="gm-card" data-testid={`plan-day-${day.id}`}>
      <div className="gm-card-head">
        <h4 className="gm-card-title">{dayName}</h4>
        {exercises.length > 0 && (
          <div className="gm-chip-row">
            <span className="gm-chip"><Clock size={14} aria-hidden="true" /> ~{minutes} min</span>
            <span className="gm-chip"><Dumbbell size={14} aria-hidden="true" /> {plural(exercises.length, 'exercício', 'exercícios')}</span>
          </div>
        )}
      </div>

      {exercises.length === 0 ? <p className="gm-note">Este dia ainda não tem exercícios.</p> : (
        <ol className="gm-list">
          {exercises.map((e, i) => (
            <li key={e.id ?? i} className="gm-list-row gm-plan-row">
              <ExerciseOpen exercise={e} onOpen={setFicheId}>
                <Thumb url={exerciseImage(e)} />
                <span className="gm-list-main">
                  <span className="gm-list-title gm-list-name">{e.name}{e.isLegacy ? ' · legado' : ''}</span>
                  <span className="gm-list-sub">{prescriptionLine(e)}</span>
                </span>
              </ExerciseOpen>
              {/* As ações do dia, num sítio só: quando não cabem ao lado do nome, passam para a linha de baixo. */}
              <span className="gm-plan-actions" data-testid={`plan-actions-${day.id}`}>
                {/* Sem ligação à biblioteca não há sugestões do servidor: não se mostra uma ação que não teria o que mostrar. */}
                {editable && Number.isInteger(e.exerciseLibraryId) ? (
                  <button type="button" className="gm-btn gm-btn-ghost" onClick={() => setSwap(e)} data-testid={`plan-replace-${e.id}`}>
                    <Repeat size={16} aria-hidden="true" /> Trocar
                  </button>
                ) : null}
                {/* Remover não precisa da biblioteca: um exercício escrito à mão sai da mesma forma. Só no plano DELE. */}
                {editable ? (
                  <button type="button" className="gm-btn gm-btn-ghost" onClick={() => { setRemoving(e); setNotice(null); setRemoveError(null); }} data-testid={`plan-remove-${e.id}`} aria-label={`Remover ${e.name}`}>
                    <Trash2 size={16} aria-hidden="true" /> Remover
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      )}

      {notice ? <p className="gm-note" role="status" data-testid={`plan-notice-${day.id}`}>{notice}</p> : null}

      {ficheId != null && <ExerciseFicheDialog exerciseId={ficheId} onClose={() => setFicheId(null)} />}

      {/* A confirmação diz o que sai — o exercício, por nome, e o dia — antes de o servidor ser tocado. */}
      {removing ? (
        <div className="gm-owned-note" role="group" aria-label="Confirmar remoção" data-testid={`plan-remove-confirm-${removing.id}`}>
          <p className="gm-note">
            Remover «{removing.name}» de {dayName}? O exercício sai do plano e a prescrição dele perde-se.
          </p>
          {removeError ? <p className="gm-error" role="alert" data-testid={`plan-remove-error-${removing.id}`}>{removeError}</p> : null}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="gm-btn gm-btn-ghost" onClick={() => setRemoving(null)} disabled={busy}>Não remover</button>
            <button type="button" className="gm-btn gm-btn-primary" onClick={() => remove(removing)} disabled={busy} data-testid={`plan-remove-yes-${removing.id}`}>Remover</button>
          </div>
        </div>
      ) : null}

      {editable ? (
        <button type="button" className="gm-btn gm-btn-ghost" onClick={() => setAddOpen(true)} data-testid={`plan-add-exercise-${day.id}`} style={{ marginTop: 12 }}>
          <Plus size={18} aria-hidden="true" /> Adicionar exercício
        </button>
      ) : null}

      <button
        type="button"
        className="gm-btn gm-btn-primary"
        onClick={() => onStart(day.id)}
        disabled={exercises.length === 0}
        data-testid={`plan-start-${day.id}`}
        style={{ marginTop: 12 }}
      >
        <Play size={18} fill="currentColor" aria-hidden="true" /> Começar este dia
      </button>

      {editable ? (
        <>
          <AddExerciseSheet
            open={addOpen}
            onClose={() => setAddOpen(false)}
            onAdd={add}
            busy={busy}
            inSessionIds={libraryIds}
            withPrescription
            subtitle={`Para ${dayName} · fica guardado no seu plano`}
            footerNote="Fica no fim do dia, com a prescrição que indicar."
          />
          {swap ? (
            <SubstituteSheet
              open
              exercise={{ id: swap.id, name: swap.name, plannedName: swap.name, plannedLibraryExerciseId: swap.exerciseLibraryId }}
              onClose={() => setSwap(null)}
              onConfirm={(libraryExerciseId) => replace(libraryExerciseId)}
              busy={busy}
              inSessionIds={libraryIds.filter((id) => id !== swap.exerciseLibraryId)}
              subtitle={`Em vez de ${swap.name} · neste dia do seu plano`}
              footerNote="A troca fica guardada no seu plano."
              allowReason={false}
              emptyHint={EMPTY_IN_PLAN}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function PlanCard({ plan, onStartDay, editable, fromGym, onGenerateMine, onChanged }) {
  const [open, setOpen] = useState(false);
  const days = plan.workoutDays || [];
  const structured = days.some((d) => (d.exercises || []).length > 0);
  const isAi = /\bAI\b|\bIA\b/.test(plan.title || '') || /AI Personal Trainer/i.test(plan.notes || '');
  // Um plano ativo abre-se sozinho: é o que o atleta vem ver. Os outros ficam fechados, sem esconder que existem.
  const [expanded, setExpanded] = useState(!!plan.isActive && structured);

  return (
    <article className="gm-card" data-testid={`plan-${plan.id}`}>
      <div className="gm-card-head">
        <h3 className="gm-card-title">{plan.title || plan.name}</h3>
        <div className="gm-chip-row">
          {isAi ? <span className="gm-chip is-info">Criado pela IA</span> : null}
          <span className={`gm-chip ${plan.isActive ? 'is-good' : ''}`}>{plan.isActive ? 'Ativo' : 'Inativo'}</span>
          {plan.planType === 'Template' ? <span className="gm-chip">Modelo</span> : null}
        </div>
      </div>

      {/* Plano do GINÁSIO: é do treinador que o atribuiu. As ações de edição não aparecem — em vez delas, uma linha
          curta a dizer de quem é e onde é que ele pode ter o plano dele. */}
      {fromGym ? (
        <div className="gm-owned-note" data-testid={`plan-gym-${plan.id}`}>
          <p className="gm-note">
            Este plano é do ginásio: quem o altera é o seu treinador. Se quiser um plano seu, gerado a partir do seu objetivo e dos dias que treina, use «Gerar o meu treino».
          </p>
          <button type="button" className="gm-btn gm-btn-ghost" onClick={onGenerateMine} data-testid="plan-generate-mine">
            <Sparkles size={18} aria-hidden="true" /> Gerar o meu treino
          </button>
        </div>
      ) : null}

      {structured ? (
        <>
          <p className="gm-note">
            {plural(days.length, 'dia', 'dias')}
            {days.length > 1 ? ' · cada dia treina-se por si' : ''}
            {plan.description ? ` · ${plan.description}` : ''}
          </p>

          {expanded ? (
            <div className="gm-grid">
              {days.map((d, i) => (
                <div key={d.id} className="gm-col-6">
                  <PlanDay day={d} index={i} planId={plan.id} editable={editable} onStart={onStartDay} onChanged={onChanged} />
                </div>
              ))}
            </div>
          ) : null}

          {days.length > 0 ? (
            <button type="button" className="gm-card-link" onClick={() => setExpanded((v) => !v)}>
              {expanded ? <>Ver menos <ChevronUp size={14} aria-hidden="true" /></> : <>Ver os {days.length} dias <ChevronDown size={14} aria-hidden="true" /></>}
            </button>
          ) : null}
        </>
      ) : plan.description ? (
        <>
          <p className="gm-preline gm-note" style={open ? undefined : { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{plan.description}</p>
          {plan.description.length > 220 ? (
            <button type="button" className="gm-card-link" onClick={() => setOpen((v) => !v)}>
              {open ? <>Ver menos <ChevronUp size={14} aria-hidden="true" /></> : <>Ver tudo <ChevronDown size={14} aria-hidden="true" /></>}
            </button>
          ) : null}
        </>
      ) : <p className="gm-note">Sem descrição.</p>}
    </article>
  );
}

function PlanTab() {
  const navigate = useNavigate();
  const [state, setState] = useState('loading');
  const [plans, setPlans] = useState([]);
  const [meId, setMeId] = useState(null);      // o atleta da sessão (o servidor diz quem é)
  const [canEditAssigned, setCanEditAssigned] = useState(false);   // a permissão da ficha do utilizador (o servidor é que decide)
  const [genOpen, setGenOpen] = useState(false);   // «Gerar o meu treino», a partir do plano do ginásio

  // `silent` recarrega sem esqueleto: é o que se usa depois de acrescentar/trocar (o plano fica à vista, atualizado).
  const load = useCallback((opts = {}) => {
    if (!opts.silent) setState('loading');
    return workoutPlans.getAll({})
      .then((r) => {
        setPlans(Array.isArray(r.data) ? r.data : []);
        setState('ok');
        if (!opts.silent) {
          // abrir o plano é vê-lo: o número no menu deixa de contar os planos novos
          try { localStorage.setItem('seen.workoutAt', new Date().toISOString()); window.dispatchEvent(new Event('updateBadges')); } catch { /* sem armazenamento */ }
        }
      })
      .catch(() => setState('error'));
  }, []);

  useEffect(() => { load(); }, [load]);

  // De quem é o plano não se adivinha: pergunta-se ao servidor quem está autenticado. Sem essa resposta, não se
  // mostra ação nenhuma (um botão que o servidor recusaria é pior do que não o ter).
  useEffect(() => {
    let alive = true;
    auth.me()
      .then((r) => { if (alive) { setMeId(r.data?.id ?? null); setCanEditAssigned(r.data?.canEditAssignedWorkout === true); } })
      .catch(() => { if (alive) { setMeId(null); setCanEditAssigned(false); } });
    return () => { alive = false; };
  }, []);

  if (state === 'loading') return <div className="gm-skeleton" aria-busy="true" aria-label="A carregar os planos" />;
  if (state === 'error') return <div className="gm-error" role="alert">Não foi possível carregar os planos.</div>;
  if (plans.length === 0) {
    return <div className="gm-empty"><strong>Ainda não tem planos de treino</strong>Quando o seu treinador lhe atribuir um plano, aparece aqui.</div>;
  }

  const sorted = [...plans].sort((a, b) => Number(b.isActive) - Number(a.isActive) || new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // Começar um dia a partir do plano: o mesmo caminho do ecrã «Hoje» (o player arranca sem segundo «Começar»).
  return (
    <div className="gm-grid">
      {sorted.map((p) => {
        const { editable, fromGym } = planOwnership(p, meId, canEditAssigned);
        return (
          <div key={p.id} className="gm-col-12">
            <PlanCard
              plan={p}
              editable={editable}
              fromGym={fromGym}
              onGenerateMine={() => setGenOpen(true)}
              onChanged={() => load({ silent: true })}
              onStartDay={(dayId) => navigate('/treino', { state: { start: true, dayId, why: null } })}
            />
          </div>
        );
      })}
      {genOpen ? (
        <MyPlanGeneratorDialog
          onClose={() => setGenOpen(false)}
          onSaved={() => { setGenOpen(false); toast.success('Plano guardado. Está aqui, no seu plano.'); load({ silent: true }); }}
        />
      ) : null}
    </div>
  );
}

// ── Histórico ─────────────────────────────────────────────────────────────

const STATUS = { Completed: ['Concluído', 'is-good'], Abandoned: ['Abandonado', 'is-warn'], InProgress: ['Em curso', 'is-info'], Partial: ['Parcial', ''] };

function HistoryTab() {
  const [state, setState] = useState('loading');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE = 20;

  useEffect(() => {
    workoutExecution.history(0, PAGE)
      .then((r) => { setItems(r.data?.items || []); setTotal(r.data?.total || 0); setState('ok'); })
      .catch(() => setState('error'));
  }, []);

  const more = () => {
    setLoadingMore(true);
    workoutExecution.history(items.length, PAGE)
      .then((r) => setItems((cur) => [...cur, ...(r.data?.items || [])]))
      .catch(() => toast.error('Não foi possível carregar mais treinos.'))
      .finally(() => setLoadingMore(false));
  };

  if (state === 'loading') return <div className="gm-skeleton" aria-busy="true" aria-label="A carregar o histórico" />;
  if (state === 'error') return <div className="gm-error" role="alert">Não foi possível carregar o histórico.</div>;
  if (items.length === 0) return <div className="gm-empty"><strong>Ainda não fez nenhum treino</strong>Os treinos que concluir ficam aqui, com a duração e o volume.</div>;

  return (
    <section className="gm-card" aria-label="Treinos feitos">
      <ul className="gm-list" data-testid="history-list">
        {items.map((h) => {
          const [label, tone] = STATUS[h.status] || [h.status, ''];
          return (
            <li key={h.id} className="gm-list-row">
              <span className="gm-list-main">
                <span className="gm-list-title">{h.dayName || 'Treino'}</span>
                <span className="gm-list-sub">
                  {fmtDate(h.performedAt)}
                  {h.durationSeconds ? ` · ${formatDuration(h.durationSeconds)}` : ''}
                  {h.setsLogged ? ` · ${plural(h.setsLogged, 'série', 'séries')}` : ''}
                  {Number(h.volumeKg) > 0 ? ` · ${Math.round(Number(h.volumeKg)).toLocaleString('pt-PT')} kg` : ''}
                  {h.estimatedCaloriesKcal ? ` · ${h.estimatedCaloriesKcal.toLocaleString('pt-PT')} kcal` : ''}
                  {h.planTitle ? ` · ${h.planTitle}` : ''}
                </span>
              </span>
              <span className={`gm-chip ${tone}`}>{label}</span>
            </li>
          );
        })}
      </ul>
      {items.length < total ? (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button type="button" className="gm-btn gm-btn-ghost gm-btn-sm" onClick={more} disabled={loadingMore}>{loadingMore ? 'A carregar…' : `Ver mais (${total - items.length})`}</button>
        </div>
      ) : null}
    </section>
  );
}

// ── A página ──────────────────────────────────────────────────────────────

export default function TrainingHubPage() {
  const { pathname } = useLocation();
  const tab = pathname.endsWith('/plano') ? 'plano' : pathname.endsWith('/historico') ? 'historico' : pathname.endsWith('/exercicios') ? 'exercicios' : 'hoje';

  return (
    <div className="gm-page" data-testid="training-hub">
      <nav className="gm-tabs" aria-label="Treino">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `gm-tab ${isActive ? 'is-on' : ''}`}>{t.label}</NavLink>
        ))}
      </nav>
      {tab === 'hoje' ? <TodayTab /> : tab === 'plano' ? <PlanTab /> : tab === 'exercicios' ? <MemberLibrary /> : <HistoryTab />}
    </div>
  );
}
